import { Slot } from '@radix-ui/react-slot';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import React from 'react';
import type { BoxProps } from '../box/Box';
import { Box } from '../box/Box';
import { Spinner } from '../spinner/Spinner';
import { Text } from '../text/Text';
import styles from './Settings.module.scss';
import { SettingsWizardComponents } from './Settings.Wizard';
import { SettingsFormControls } from './SettingsFormControls';

type SettingsSharedProps = {
  /** @link https://www.radix-ui.com/primitives/docs/utilities/slot */
  asChild?: boolean;
};

/**
 * Frame for settings UI
 */
const cvaFrame = cva(styles.containerFrame, {
  variants: {
    variant: {
      modal: styles['variant-in-modal'],
      fullscreen: styles['variant-fullscreen'],
      /**
       * TODO 'wizardModal' variant
       * * ref apps/timely-frontend/src/domains/integrations/components/setup_wizard/Integrations.SetupWizard.module.scss
       * * Create thin wrapper ala `Settings.Wizard`, with it's own special components?, e.g:
       *  `Wizard.Step`, `Wizard.Step.Header`, `Wizard.Step.Content`, `Wizard.Step.Footer`,
       * * Use framer-motion for transitions.
       */
    },
  },
  defaultVariants: { variant: 'fullscreen' },
});

export type SettingsProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof cvaFrame> &
  SettingsSharedProps & {};

const SettingsRoot = React.forwardRef<HTMLDivElement, SettingsProps>(
  ({ variant, asChild, ...props }, forwardedRef) => {
    const Component = asChild ? Slot : 'div';
    return (
      <Component
        {...props}
        ref={forwardedRef}
        className={cvaFrame({
          variant,
          className: props.className,
        })}
      />
    );
  }
);
SettingsRoot.displayName = 'Settings';

/**
 * Sidebar for settings UI
 */

const cvaSidebar = cva(styles.containerSidebar);

export type SettingsSidebarProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof cvaSidebar> &
  SettingsSharedProps & {};

const SettingsSidebar = React.forwardRef<HTMLDivElement, SettingsSidebarProps>(
  ({ asChild, ...props }, forwardedRef) => {
    const Component = asChild ? Slot : 'aside';
    return (
      <Component
        {...props}
        ref={forwardedRef}
        className={cvaSidebar({ className: props.className })}
      />
    );
  }
);
SettingsSidebar.displayName = 'Settings.Sidebar';

/**
 * Main for settings UI
 */

const cvaMain = cva(styles.containerMain, {
  variants: {
    variant: {
      dataBrowser: styles['variant-data-browser'],
    },
  },
});

export type SettingsMainProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof cvaMain> &
  SettingsSharedProps & {};

const SettingsMain = React.forwardRef<HTMLDivElement, SettingsMainProps>(
  ({ asChild, variant, ...props }, forwardedRef) => {
    const Component = asChild ? Slot : 'main';
    return (
      <Component
        {...props}
        ref={forwardedRef}
        className={cvaMain({ variant, className: props.className })}
      />
    );
  }
);

SettingsMain.displayName = 'Settings.Main';

/**
 * Main Header for settings UI
 */

const cvaMainHeader = cva(styles.mainHeader);

export type SettingsMainHeaderProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof cvaMainHeader> &
  SettingsSharedProps & {
    /** When set, <Text.Headline /> is automatically wrapped. Use `children` for custom styling.  */
    title?: string;
  };

const SettingsMainHeader = React.forwardRef<HTMLDivElement, SettingsMainHeaderProps>(
  ({ children, asChild, title, ...props }, forwardedRef) => {
    const Component = asChild ? Slot : 'div';
    return (
      <Component
        {...props}
        ref={forwardedRef}
        className={cvaMainHeader({ className: props.className })}
      >
        {!!title && <Text.Headline size="small">{title}</Text.Headline>}
        {children}
      </Component>
    );
  }
);

SettingsMainHeader.displayName = 'Settings.Main.Header';

/**
 * Main Content for settings UI
 */

const cvaMainContent = cva(styles.mainContent);

export type SettingsMainContentProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof cvaMainContent> &
  SettingsSharedProps & {};

const SettingsMainContent = React.forwardRef<HTMLDivElement, SettingsMainContentProps>(
  ({ asChild, ...props }, forwardedRef) => {
    const Component = asChild ? Slot : 'main';
    return (
      <Component
        {...props}
        ref={forwardedRef}
        className={cvaMainContent({ className: props.className })}
      />
    );
  }
);
SettingsMainContent.displayName = 'Settings.Main.Content';

/**
 * Empty Main Content, fit for "center/center messages/loading/etc".
 */
const EmptyMainContent = React.forwardRef<HTMLDivElement, SettingsMainContentProps>(
  ({ children, ...props }, forwardedRef) => {
    return (
      <SettingsMainContent {...props} ref={forwardedRef}>
        <Box.Column gap="large" alignItems="center" justifyContent="center" height="100%">
          {children}
        </Box.Column>
      </SettingsMainContent>
    );
  }
);
EmptyMainContent.displayName = 'Settings.Empty.MainContent';

/**
 * Main Content Loader
 */
const EmptyMainContentLoader = React.forwardRef<
  HTMLDivElement,
  SettingsMainContentProps & { loadingText?: string }
>(({ children, loadingText, ...props }, forwardedRef) => {
  return (
    <EmptyMainContent {...props} ref={forwardedRef}>
      <Spinner />
      <Text.Label color="subtle" size="xsmall">
        {loadingText || 'Loading...'}
      </Text.Label>
      {children}
    </EmptyMainContent>
  );
});
EmptyMainContent.displayName = 'Settings.Empty.MainContent';

/**
 * Main Footer for settings UI
 */

const cvaMainFooter = cva(styles.mainFooter);

export type SettingsMainFooterProps = React.HTMLAttributes<HTMLDivElement> &
  Omit<BoxProps, 'as'> &
  VariantProps<typeof cvaMainFooter> &
  SettingsSharedProps & {};

const SettingsMainFooter = React.forwardRef<HTMLDivElement, SettingsMainFooterProps>(
  ({ asChild, ...props }, forwardedRef) => {
    const Component = asChild ? Slot : Box.Row;
    return (
      <Component
        gap="large"
        {...props}
        ref={forwardedRef}
        className={cvaMainFooter({ className: props.className })}
      />
    );
  }
);

SettingsMainFooter.displayName = 'Settings.Main.Footer';

export const Settings = Object.assign(SettingsRoot, {
  Sidebar: SettingsSidebar,
  Main: Object.assign(SettingsMain, {
    Header: SettingsMainHeader,
    Content: SettingsMainContent,
    Empty: {
      Content: EmptyMainContent,
      ContentLoader: EmptyMainContentLoader,
    },
    Footer: SettingsMainFooter,
  }),
  Wizard: SettingsWizardComponents,
  FormControls: SettingsFormControls,
});

Settings.displayName = 'Settings';
